/* eslint-disable react/jsx-handler-names */

import React, { memo } from 'react'
import PropTypes from 'prop-types'
import { shallowEqual, useSelector } from 'react-redux'

import { DateTime, millisToLocaleString } from '../../helpers/dateHelpers'
import { translate } from '../../helpers/stringHelpers'

// components
import GoogleMap from '../../components/googleMap'
import Modal from '../../components/modal'
import ModalBody from '../../components/modalBody'
import ModalHeader from '../../components/modalHeader'
import Row from '../row'
import Col from '../col'
import AttachmentList from '../attachmentList'
import LazyImage from '../lazyImage'

export const SaleInformationModal = ({
  auctionUuid,
  isMarketplace,
  isOpen,
  listingUuid = null,
  onClose
}) => {
  const auction = useSelector(state => state.auctions.auctions[auctionUuid], shallowEqual)
  const listing = useSelector(state => listingUuid ? state.lots.listings[listingUuid] : {}, shallowEqual)
  const information = useSelector(state => state.auctions.information[auctionUuid], shallowEqual)
  const logo = useSelector(state => state.attachments.attachments[information?.auctionLogo]?.publicId)
  const documents = useSelector(state => information?.documents?.map(documentId => state.attachments.attachments[documentId]).filter(document => !!document))
  if (!auction || !information) return null

  const hammerTaxRate = listing?.hammerTaxRate || information.hammerTaxRate
  const buyersPremiumPercentage = listing?.buyersPremium || information.buyersPremiumPercentage
  const buyersPremiumTaxRate = listing?.buyersPremiumTaxRate || information.buyersPremiumTaxRate
  const chargesTaxRate = listing?.chargesTaxRate || information.chargesTaxRate

  const locationLatitude = listing?.address?.locationLatitude || information.locationLatitude
  const locationLongitude = listing?.address?.locationLongitude || information.locationLongitude
  const address = listing?.address?.address || information.address

  return (
    <Modal size='lg' onClose={onClose} isOpen={isOpen} id='feesInfoModal'>
      <ModalHeader onClose={onClose}>
        {translate('JspPublicCodes_JSP_AUCTION_SALE_INFORMATION_VIEW')}
      </ModalHeader>
      <ModalBody>
        {logo
          ? (
            <LazyImage
              id='sale-info__image--auction-logo'
              imageCropModeOverride='fill'
              label='auction logo'
              src={logo}
              key={`logo-image-${logo}`}
            />)
          : null}
        <h2 id='sale-info__title'>{isMarketplace ? translate('SEOCodes_PAGE_TITLE_MARKETPLACE_PAGE') : auction.title}</h2>
        <h3 id='sale-info__title--auction-information'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION')}</h3>
        <dl className='row'>
          <React.Fragment>
            {isMarketplace
              ? null
              : (
                <React.Fragment>
                  <dt id='sale-info__heading--auction-dates' key='sale-info__heading--auction-dates' className='col-xs-6 col-sm-4 text-right'>
                    {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_DATES')}
                  </dt>
                  <dd id='sale-info__content--auction-dates' key='sale-info__content--auction-dates' className='col-xs-6 col-sm-8'>
                    {translate('JspPublicCodes_JSP_TIMED_AUCTION_STARTS')} {millisToLocaleString(auction.startsAt, null, { ...DateTime.DATETIME_MED_WITH_WEEKDAY, timeZoneName: 'short' })}
                    {auction.endsAt ? (<span><br />{translate('JspPublicCodes_JSP_TIMED_AUCTION_ENDS')} {millisToLocaleString(auction.endsAt, null, { ...DateTime.DATETIME_MED_WITH_WEEKDAY, timeZoneName: 'short' })}</span>) : ''}
                  </dd>
                </React.Fragment>
                )
            }
            <dt className='col-xs-6 col-sm-4 text-right' id='sale-info__heading--viewing-dates' key='sale-info__heading--viewing-dates'>
              {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_VIEWING_DAYS')}
            </dt>
            <dd className='col-xs-6 col-sm-8' id='sale-info__content--viewing-dates' key='sale-info__content--viewing-dates'>
              {information.viewingDates && !isMarketplace
                ? ([
                <p id='sale-info__content--viewing-dates-range' key='viewingDatesDate'>
                  {millisToLocaleString(information.viewingStartDateTime, null, { ...DateTime.DATE_MED, timeZoneName: 'short' })}<br />
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_TO')} {millisToLocaleString(information.viewingEndDateTime, null, { ...DateTime.DATE_MED, timeZoneName: 'short' })}
                </p>,
                <p id='sale-info__content--viewing-times-range' key='viewingDatesTime'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_FROM')} {millisToLocaleString(information.viewingStartDateTime, null, DateTime.TIME_SIMPLE)} {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_TO')} {millisToLocaleString(information.viewingEndDateTime, null, DateTime.TIME_SIMPLE)}
                </p>
                  ])
                : null}
              {information.appointmentRequiredForViewing
                ? (
                <p id='sale-info__content--viewing-appointment-required'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_BY_APPOINTMENT')}
                </p>
                  )
                : null}
              {information.viewingInstruction
                ? (
                <p id='sale-info__content--viewing-instruction'>
                  {information.viewingInstruction}
                </p>
                  )
                : null}
            </dd>
          </React.Fragment>
          {information.address
            ? ([
            <dt key='sale-info__heading--address' id='sale-info__heading--address' className='col-xs-6 col-sm-4 text-right'>
              {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_SITE_ADDRESS')}
            </dt>,
            <dd key='sale-info__content--address' id='sale-info__content--address' className='col-xs-6 col-sm-8' dangerouslySetInnerHTML={{ __html: address }} />
              ])
            : null}
        </dl>
        {(locationLongitude && locationLatitude)
          ? (
          <div id='sale-info__container--map'>
            <GoogleMap
              longitude={locationLongitude}
              latitude={locationLatitude}
            />
          </div>
            )
          : null}
        <hr />
        {documents?.length
          ? ([
            <section key='section--downloads'>
              <h3 id='sale-info__heading--downloads'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_ADDITIONAL_DOCS')}</h3>
              <AttachmentList
                classNamePrefix='auction-downloads'
                documents={documents}
              />
            </section>,
            <hr key='rule--downloads' />
            ])
          : null}
        <h3 id='sale-info__title--fees'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_FEES_PAYMENT')}</h3>
        <span className='sale-info__content--fees-custom' dangerouslySetInnerHTML={{ __html: translate('JspPublicCodes_ESSENTIAL_INFO_FEES_CUSTOM_HTML') }} />
        {information.salesTaxRequired
          ? (
            <dl className='row'>
              <dt id='sale-info__heading--currency-code' className='col-xs-6 col-sm-4 text-right'>
                {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_CURRENCY')}
              </dt>
              <dd id='sale-info__content--currency-code' className='col-xs-6 col-sm-8'>
                {information.currencyCode}
              </dd>
              {buyersPremiumPercentage
                ? ([
                <dt id='sale-info__heading--buyers-premium-percentage' key='buyersPremiumLabel' className='col-xs-6 col-sm-4 text-right'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_BUYERS_PREMIUM')}
                </dt>,
                <dd id='sale-info__content--buyers-premium-percentage' key='buyersPremiumValue' className='col-xs-6 col-sm-8'>
                  {buyersPremiumPercentage}% {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_OR_AS_SPECIFIED')}
                </dd>
                  ])
                : null}

              {hammerTaxRate
                ? ([
                <dt id='sale-info__heading--hammer-tax' key='hammerTaxRateLabel' className='col-xs-6 col-sm-4 text-right'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_HAMMER_TAX_RATE')}
                </dt>,
                <dd id='sale-info__content--hammer-tax' key='hammerTaxRateValue' className='col-xs-6 col-sm-8'>
                  {hammerTaxRate}% {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_OR_AS_SPECIFIED')}
                </dd>
                  ])
                : null}

              {buyersPremiumTaxRate
                ? ([
                <dt id='sale-info__heading--buyers-premium-tax' key='buyersPremiumTaxRateLabel' className='col-xs-6 col-sm-4 text-right'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_BP_TAX_RATE')}
                </dt>,
                <dd id='sale-info__content--buyers-premium-tax' key='buyersPremiumTaxRateValue' className='col-xs-6 col-sm-8'>
                  {buyersPremiumTaxRate}% {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_OR_AS_SPECIFIED')}
                </dd>
                  ])
                : null}

              {chargesTaxRate
                ? ([
                <dt id='sale-info__heading--charges-tax' key='chargesTaxRateLabel' className='col-xs-6 col-sm-4 text-right'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_CHARGES_TAX_RATE')}
                </dt>,
                <dd id='sale-info__content--charges-tax' key='chargesTaxRateValue' className='col-xs-6 col-sm-8'>
                  {chargesTaxRate}% {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_OR_AS_SPECIFIED')}
                </dd>
                  ])
                : null}
            </dl>
            )
          : (
            <div
              id='sale-info__content--tax-not-required' dangerouslySetInnerHTML={{
                __html: translate('JspPublicCodes_JSP_AUCTION_INFORMATION_SALES_TAX_NOT_REQUIRED_MESSAGE')
              }}
            />
            )}
        <Row>
          <Col xs={12}>
            <h4 id='sale-info__heading--payment'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_PAYMENT')}</h4>
            <p id='sale-info__content--payment' dangerouslySetInnerHTML={{ __html: information.paymentInformation }} />
          </Col>
        </Row>
        <hr />
        <h3 id='sale-info__title--goods'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_GOODS')}</h3>
        {information.clearanceDates || information.clearanceInstructions
          ? (
          <Row>
            <Col xs={12}>
              <h4 id='sale-info__heading--clearance-days'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_CLEARANCE_DAYS')}</h4>
              {information.clearanceDates
                ? ([
                <p key='clearanceDates' id='sale-info__content--clearance-dates'>{millisToLocaleString(information.clearanceStartDateTime, null, DateTime.DATE_MED)} - {millisToLocaleString(information.clearanceEndDateTime, null, { ...DateTime.DATE_MED, timeZoneName: 'short' })}</p>,
                <p key='clearanceTimes' id='sale-info__content--clearance-times'>
                  {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_FROM')} {millisToLocaleString(information.clearanceStartDateTime, null, DateTime.TIME_SIMPLE)} {translate('JspPublicCodes_JSP_AUCTION_INFORMATION_TO')} {millisToLocaleString(information.clearanceEndDateTime, null, DateTime.TIME_SIMPLE)}
                </p>
                  ])
                : null}
              {information.clearanceInstructions
                ? (
                <p id='sale-info__content--clearance-instruction'>
                  {information.clearanceInstructions}
                </p>
                  )
                : null}
            </Col>
          </Row>
            )
          : null}
        <Row>
          <Col xs={12}>
            <h4 id='sale-info__heading--collection'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_COLLECTION')}</h4>
            <p id='sale-content--collection' dangerouslySetInnerHTML={{ __html: information.collectionInformation }} />
          </Col>
        </Row>
        {information.contactInformation
          ? ([
          <hr key='contactDivider' />,
          <h3 id='sale-info__heading--contact' key='contactTitle'>{translate('JspPublicCodes_JSP_AUCTION_INFORMATION_CONTACTS')}</h3>,
          <div
            id='sale-info__content--contact'
            key='contactContent' dangerouslySetInnerHTML={{
              __html: information.contactInformation
            }}
          />
            ])
          : null}
      </ModalBody>
    </Modal>
  )
}

SaleInformationModal.defaultProps = {
  isOpen: false
}

SaleInformationModal.propTypes = {
  auctionUuid: PropTypes.string.isRequired,
  isMarketplace: PropTypes.bool,
  isOpen: PropTypes.bool,
  listingUuid: PropTypes.string,
  onClose: PropTypes.func.isRequired
}

const isEqual = (a, b) => a.auctionUuid === b.auctionUuid &&
  a.listingUuid === b.listingUuid &&
  a.isOpen === b.isOpen

export default memo(SaleInformationModal, isEqual)
